import type {
  ChapterSummary,
  ContentCatalog,
  LevelSummary,
  NetStatusSnapshot,
  ProgressState,
  WorldSummary,
} from '@md/shared';
import { EMPTY_PROGRESS, findCatalogLevel, MAX_STARS } from '@md/shared';
import { formatStars } from '../game/hud';
import { type ChapterUnlockState, computeUnlockState } from '../progress';
import { button, el } from '../ui/dom';

/**
 * Écran de SÉLECTION world → chapitre → niveau (concept §4.1, spec §2).
 *
 * PHASE 4 : la liste vient du **catalogue servi par l'API** (donc de SQLite).
 * PHASE 5 : en mode local, elle applique la **progression** du joueur
 * (concept §4.3) — étoiles gagnées, niveau suivant débloqué par la victoire du
 * précédent, chapitre suivant débloqué quand le précédent est terminé.
 *
 * Deux usages :
 *  - `net` : seul l'**hôte** choisit, l'invité voit le niveau retenu et se
 *    déclare prêt. Le verrouillage n'y est PAS appliqué : la progression est
 *    locale à un appareil, elle n'a pas de sens partagée (cf. rapport phase 5) ;
 *  - `local` : le joueur choisit un niveau débloqué et la partie démarre.
 *
 * Les niveaux `draft` n'apparaissent que si le catalogue a été demandé en mode
 * brouillons (`?drafts=<token>`), sont marqués comme tels, et restent toujours
 * jouables (ce sont des niveaux de test).
 */
export interface LevelSelectScreen {
  readonly element: HTMLElement;
  /** Remplace le catalogue affiché. */
  setCatalog(catalog: ContentCatalog | null): void;
  /** Remplace la progression appliquée (mode `local`). */
  setProgress(progress: ProgressState): void;
  /** Met à jour l'état réseau (mode `net` uniquement). */
  update(status: NetStatusSnapshot): void;
}

export type LevelSelectMode = 'net' | 'local';

export interface LevelSelectScreenOptions {
  readonly mode: LevelSelectMode;
  readonly onPickLevel: (levelId: string) => void;
  readonly onReady?: (ready: boolean) => void;
  readonly onLeave: () => void;
  readonly leaveLabel?: string;
}

export function createLevelSelectScreen(options: LevelSelectScreenOptions): LevelSelectScreen {
  const isNet = options.mode === 'net';
  const view = el('main', 'screen screen--levels');
  view.dataset.mode = options.mode;
  view.append(el('h1', 'lobby__title', 'Choix du niveau'));

  const worldSelect = el('select', 'select');
  worldSelect.dataset.testid = 'world-select';

  const chapterSelect = el('select', 'select');
  chapterSelect.dataset.testid = 'chapter-select';

  const levelList = el('div', 'levels');
  levelList.dataset.testid = 'level-list';

  const summary = el('p', 'lobby__hint');
  summary.dataset.testid = 'level-summary';

  // L'histoire du niveau mis en avant : la trame de l'évasion, un épisode à la
  // fois. En réseau c'est le niveau retenu par l'hôte ; en local, le premier
  // niveau jouable du chapitre, donc celui que le duo s'apprête à lancer.
  const story = el('p', 'levels__story');
  story.dataset.testid = 'level-story';
  story.hidden = true;

  const readyButton = button('Je suis prêt !', 'ready', 'button button--primary');
  const startButton = button('Démarrer la partie', 'start', 'button button--primary');
  const hint = el('p', 'lobby__hint');
  hint.dataset.testid = 'levels-hint';

  const leave = button(
    options.leaveLabel ?? 'Quitter la partie',
    'levels-leave',
    'button button--ghost',
  );
  leave.addEventListener('click', () => options.onLeave());

  const actions = el('div', 'levels__actions');
  if (isNet) {
    actions.append(readyButton, startButton);
  }

  // La description du world, sous les deux menus : l'identité du monde est
  // annoncée dès la sélection, avant même de charger un niveau.
  const worldDescription = el('p', 'levels__world-desc');
  worldDescription.dataset.testid = 'world-description';

  const selection = el('div', 'levels__selection');
  selection.append(worldSelect, chapterSelect, worldDescription);

  const draftsBadge = el(
    'p',
    'badge',
    '🔧 Mode brouillons : les niveaux non publiés sont visibles',
  );
  draftsBadge.dataset.testid = 'drafts-banner';
  draftsBadge.hidden = true;

  const progressBadge = el('p', 'levels__progress');
  progressBadge.dataset.testid = 'chapter-progress';

  view.append(
    draftsBadge,
    selection,
    progressBadge,
    levelList,
    summary,
    story,
    actions,
    hint,
    leave,
  );

  let catalog: ContentCatalog | null = null;
  let progress: ProgressState = EMPTY_PROGRESS;
  let currentLevelId: string | null = null;
  /** Niveau dont l'histoire est affichée en mode local (cf. `renderLevels`). */
  let storyLevelId: string | null = null;
  let isHost = !isNet;
  let amReady = false;

  const currentWorld = (): WorldSummary | undefined =>
    catalog?.worlds.find((world) => world.id === worldSelect.value) ?? catalog?.worlds[0];

  const currentChapter = (): ChapterSummary | undefined =>
    currentWorld()?.chapters.find((chapter) => String(chapter.chapter) === chapterSelect.value);

  /** État de déblocage du world affiché — vide en mode réseau (pas de verrous). */
  const unlockStates = (): readonly ChapterUnlockState[] => {
    const world = currentWorld();
    if (isNet || !world) {
      return [];
    }
    return computeUnlockState(catalog, world.id, progress);
  };

  const chapterState = (chapter: number): ChapterUnlockState | undefined =>
    unlockStates().find((state) => state.chapter === chapter);

  const renderWorldDescription = (): void => {
    const description = currentWorld()?.description ?? '';
    worldDescription.textContent = description;
    worldDescription.hidden = description.length === 0;
  };

  const renderWorlds = (): void => {
    const previous = worldSelect.value;
    worldSelect.replaceChildren();
    for (const world of catalog?.worlds ?? []) {
      const option = el('option', undefined, world.name);
      option.value = world.id;
      worldSelect.append(option);
    }
    const values = [...worldSelect.options].map((option) => option.value);
    worldSelect.value = values.includes(previous) ? previous : (values[0] ?? '');
    renderWorldDescription();
  };

  const renderChapters = (): void => {
    const world = currentWorld();
    const previous = chapterSelect.value;
    const states = unlockStates();
    chapterSelect.replaceChildren();
    for (const chapter of world?.chapters ?? []) {
      const state = states.find((entry) => entry.chapter === chapter.chapter);
      const locked = state !== undefined && !state.unlocked;
      const stars = state ? ` · ${state.stars}/${state.maxStars}★` : '';
      const option = el(
        'option',
        undefined,
        `${locked ? '🔒 ' : ''}${chapter.chapter}. ${chapter.name} ${'★'.repeat(chapter.difficulty)}${stars}`,
      );
      option.value = String(chapter.chapter);
      option.disabled = chapter.levels.length === 0 || locked;
      option.dataset.locked = String(locked);
      chapterSelect.append(option);
    }
    const available = [...chapterSelect.options].filter((option) => !option.disabled);
    chapterSelect.value =
      available.some((option) => option.value === previous) && previous
        ? previous
        : (available[0]?.value ?? '');
  };

  const findLevel = (levelId: string | null): LevelSummary | undefined =>
    catalog && levelId ? findCatalogLevel(catalog, levelId) : undefined;

  /**
   * L'histoire du niveau mis en avant. Vide → masquée : un niveau sans
   * description (brouillon MCP, banc d'essai) ne laisse pas de trou.
   */
  const renderStory = (): void => {
    const levelId = isNet ? currentLevelId : storyLevelId;
    const description = findLevel(levelId)?.description ?? '';
    story.textContent = description;
    story.hidden = description.length === 0;
    // `data-story-level`, PAS `data-level-id` : ce dernier identifie les CARTES
    // de niveau, et les tests les cherchent par `[data-level-id="…"]`. Réutiliser
    // le nom ici ferait résoudre ce sélecteur à deux éléments.
    story.dataset.storyLevel = levelId ?? '';
  };

  const renderLevels = (): void => {
    const chapter = currentChapter();
    const state = chapter ? chapterState(chapter.chapter) : undefined;
    levelList.replaceChildren();
    let unlockedCount = 0;
    /** Mode local : le niveau dont on raconte l'histoire (premier jouable). */
    let firstPlayable: string | null = null;
    for (const level of chapter?.levels ?? []) {
      const entry = state?.levels.find((candidate) => candidate.levelId === level.id);
      const locked = entry !== undefined && !entry.unlocked;
      const stars = entry?.stars ?? 0;
      const card = button(level.name, `level-${level.id}`, 'level-card');
      card.dataset.levelId = level.id;
      card.dataset.status = level.status;
      card.dataset.selected = String(level.id === currentLevelId);
      card.dataset.locked = String(locked);
      card.dataset.stars = String(stars);
      card.disabled = locked || !isHost;
      if (!locked) {
        unlockedCount += 1;
        firstPlayable ??= level.id;
      }
      card.append(el('span', 'level-card__par', `par ${level.parTimeSeconds}s`));
      card.append(
        el(
          'span',
          locked ? 'level-card__lock' : 'level-card__stars',
          locked ? '🔒' : formatStars(stars, MAX_STARS),
        ),
      );
      if (level.status === 'draft') {
        card.append(el('span', 'level-card__draft', 'brouillon'));
      }
      card.addEventListener('click', () => {
        if (!card.disabled) {
          options.onPickLevel(level.id);
        }
      });
      levelList.append(card);
    }
    levelList.dataset.count = String(chapter?.levels.length ?? 0);
    levelList.dataset.unlocked = String(unlockedCount);

    if (!isNet) {
      // L'histoire affichée est celle du premier niveau JOUABLE du chapitre :
      // celui que le duo va lancer, donc le prochain épisode de l'évasion. Les
      // niveaux verrouillés gardent la leur pour eux — on ne se spoile pas.
      storyLevelId = firstPlayable ?? chapter?.levels[0]?.id ?? null;
      progressBadge.textContent = state
        ? `Chapitre ${state.chapter} — ${state.stars}/${state.maxStars} étoiles`
        : '';
      progressBadge.dataset.stars = String(state?.stars ?? 0);
      progressBadge.dataset.maxStars = String(state?.maxStars ?? 0);
    } else {
      progressBadge.hidden = true;
    }
    renderStory();
  };

  const renderAll = (): void => {
    renderWorlds();
    renderChapters();
    renderLevels();
  };

  worldSelect.addEventListener('change', () => {
    renderWorldDescription();
    renderChapters();
    renderLevels();
  });
  chapterSelect.addEventListener('change', () => renderLevels());
  readyButton.addEventListener('click', () => options.onReady?.(!amReady));
  startButton.addEventListener('click', () => options.onReady?.(true));

  const levelName = (levelId: string | null): string =>
    levelId ? (findLevel(levelId)?.name ?? levelId) : 'aucun';

  renderAll();

  return {
    element: view,

    setCatalog(next: ContentCatalog | null): void {
      catalog = next;
      draftsBadge.hidden = !next?.includesDrafts;
      renderAll();
      if (!isNet) {
        summary.textContent = 'Choisissez un niveau pour lancer une partie locale.';
        hint.textContent =
          'Deux joueurs, un seul clavier. Terminez un niveau pour ouvrir le suivant.';
      }
    },

    setProgress(next: ProgressState): void {
      progress = next;
      renderChapters();
      renderLevels();
    },

    update(status: NetStatusSnapshot): void {
      if (!isNet) {
        return;
      }
      const changedHost = isHost !== status.host;
      isHost = status.host;
      const me = status.players.find((player) => player.memberId === status.memberId);
      amReady = me?.ready ?? false;
      const partner = status.players.find((player) => player.memberId !== status.memberId);

      if (status.levelId !== currentLevelId || changedHost) {
        currentLevelId = status.levelId;
        renderLevels();
      }
      for (const card of levelList.querySelectorAll<HTMLButtonElement>('[data-level-id]')) {
        card.dataset.selected = String(card.dataset.levelId === currentLevelId);
        card.disabled = !isHost;
      }

      summary.textContent = `Niveau retenu : ${levelName(currentLevelId)}`;
      summary.dataset.levelId = currentLevelId ?? '';

      readyButton.hidden = isHost;
      startButton.hidden = !isHost;
      readyButton.textContent = amReady ? 'Prêt ✓ (annuler)' : 'Je suis prêt !';
      readyButton.dataset.ready = String(amReady);
      const partnerReady = partner?.ready ?? false;
      startButton.disabled = currentLevelId === null || !partnerReady;
      // Le bouton DIT pourquoi il est inactif : sans cela, l'hôte cherche quoi
      // cliquer pendant que son partenaire n'a pas encore touché « prêt ».
      startButton.textContent = partnerReady
        ? 'Démarrer la partie'
        : 'En attente de l’autre joueur — prêt…';
      startButton.dataset.ready = String(amReady);
      startButton.dataset.partnerReady = String(partnerReady);

      hint.textContent = isHost
        ? currentLevelId === null
          ? 'Choisissez un niveau.'
          : partner?.ready
            ? 'Votre partenaire est prêt : lancez la partie !'
            : 'En attente de votre partenaire…'
        : currentLevelId === null
          ? 'L’hôte choisit le niveau…'
          : amReady
            ? 'En attente du lancement par l’hôte…'
            : 'Déclarez-vous prêt quand vous voulez.';
    },
  };
}
